<script>
import Layout from '@layouts/main'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

import estoqueService from '@/src/services/api-receituario/estoque'
import produtoService from '@/src/services/produto_estoque'
import {
  buildStockSummary,
  formatMovementDate,
} from './stock-helpers'

export default {
  components: { Layout, Loading },
  data() {
    return {
      isLoading: false,
      filtro: '',
      apenasCriticos: true,
      resumoProdutos: [],
      fields: [
        { key: 'nome', label: 'Produto' },
        { key: 'disponivel', label: 'Atual', class: 'text-right' },
        { key: 'minimo', label: 'Minimo', class: 'text-right' },
        { key: 'maximo', label: 'Maximo', class: 'text-right' },
        { key: 'suggestedQuantity', label: 'Sugestao', class: 'text-right' },
        { key: 'statusLabel', label: 'Status' },
        { key: 'ultima_movimentacao', label: 'Ultima movimentacao' },
        { key: 'actions', label: '' },
      ],
    }
  },
  mounted() {
    this.carregarSugestoes()
  },
  computed: {
    itensFiltrados() {
      return this.resumoProdutos.filter((item) => {
        const matchesName = item.nome
          .toLowerCase()
          .includes(this.filtro.toLowerCase())
        const matchesCritical = this.apenasCriticos
          ? item.status === 'low' || item.status === 'empty'
          : true

        return matchesName && matchesCritical
      })
    },
    totalSugerido() {
      return this.itensFiltrados.reduce(
        (total, item) => total + item.suggestedQuantity,
        0
      )
    },
    quantidadeItens() {
      return this.itensFiltrados.length
    },
  },
  methods: {
    async carregarSugestoes() {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true

      try {
        const [movimentacaoResponse, produtoResponse] = await Promise.all([
          estoqueService.listar(token),
          produtoService.listar(token),
        ])

        this.resumoProdutos = buildStockSummary(
          movimentacaoResponse.data.estoques || [],
          produtoResponse.data.produtos || []
        )
      } catch (error) {
        this.$bvToast.toast(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
            'Nao foi possivel carregar as sugestoes de reposicao.',
          {
            variant: 'danger',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          }
        )
      } finally {
        this.isLoading = false
      }
    },
    formatDate(value) {
      return formatMovementDate(value)
    },
    statusClass(status) {
      return `stock-status-badge is-${status}`
    },
    goToAdjustment(item) {
      this.$router.push({
        path: '/estoque/ajuste-estoque',
        query: item.id_produto ? { produto: String(item.id_produto) } : {},
      })
    },
  },
}
</script>

<template>
  <Layout>
    <div class="stock-page-shell">
      <div class="stock-page-hero compact">
        <div>
          <span class="stock-page-eyebrow">Planejamento</span>
          <h1 class="stock-page-title">Sugestao de Reposicao</h1>
          <p class="stock-page-subtitle">
            Lista simples para saber o que precisa recompor usando os limites
            minimos e maximos do estoque atual.
          </p>
        </div>
        <div class="stock-page-actions">
          <button class="btn btn-outline-primary" @click="$router.push('/cadastros/produto')">
            Cadastro de produto
          </button>
          <button class="btn btn-webreceita" @click="$router.push('/estoque/movimentacao')">
            Ver movimentacoes
          </button>
        </div>
      </div>

      <div class="stock-suggestion-summary">
        <article class="summary-card">
          <span class="summary-label">Itens sugeridos</span>
          <strong class="summary-value">{{ quantidadeItens }}</strong>
        </article>
        <article class="summary-card">
          <span class="summary-label">Quantidade sugerida</span>
          <strong class="summary-value">{{ totalSugerido }}</strong>
        </article>
        <article class="summary-card helper">
          <span class="summary-label">Regra aplicada</span>
          <strong class="summary-helper">
            Quando o saldo fica abaixo do minimo, a tela sugere recompor ate o
            maximo configurado.
          </strong>
        </article>
      </div>

      <section class="stock-panel">
        <div class="stock-filters">
          <b-form-input
            v-model="filtro"
            placeholder="Filtrar por produto"
          />
          <b-form-checkbox v-model="apenasCriticos" switch>
            Mostrar apenas itens criticos
          </b-form-checkbox>
        </div>

        <b-table
          small
          responsive
          hover
          :items="itensFiltrados"
          :fields="fields"
          show-empty
          class="stock-table"
        >
          <template #empty>
            <div class="stock-empty-state">
              Nenhum produto encontrado com os filtros atuais.
            </div>
          </template>
          <template #cell(statusLabel)="row">
            <span :class="statusClass(row.item.status)">
              {{ row.item.statusLabel }}
            </span>
          </template>
          <template #cell(ultima_movimentacao)="row">
            {{ formatDate(row.item.ultima_movimentacao) }}
          </template>
          <template #cell(actions)="row">
            <button class="btn btn-light btn-sm" @click="goToAdjustment(row.item)">
              Ajustar
            </button>
          </template>
        </b-table>
      </section>

      <b-overlay :show="isLoading" no-wrap />
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.stock-page-shell {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.stock-page-hero,
.stock-panel,
.summary-card {
  background: #fff;
  border: 1px solid rgba(0, 142, 117, 0.14);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(15, 61, 52, 0.06);
}

.stock-page-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 26px 28px;
}

.stock-page-hero.compact {
  align-items: center;
}

.stock-page-eyebrow {
  display: inline-block;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #008e75;
}

.stock-page-title {
  margin: 0 0 8px;
  font-size: 32px;
  color: #11493f;
}

.stock-page-subtitle {
  margin: 0;
  color: #5f7a74;
}

.stock-page-actions {
  display: flex;
  gap: 12px;
}

.stock-suggestion-summary {
  display: grid;
  grid-template-columns: 220px 220px minmax(0, 1fr);
  gap: 18px;
}

.summary-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
}

.summary-card.helper {
  background: linear-gradient(180deg, #f4fbf9 0%, #ffffff 100%);
}

.summary-label {
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 700;
  color: #68857f;
}

.summary-value {
  font-size: 34px;
  line-height: 1;
  color: #0d6555;
}

.summary-helper {
  font-size: 15px;
  line-height: 1.5;
  color: #4f6c66;
}

.stock-panel {
  padding: 24px;
}

.stock-filters {
  display: grid;
  grid-template-columns: minmax(0, 320px) auto;
  align-items: center;
  gap: 18px;
  margin-bottom: 20px;
}

.stock-table::v-deep table {
  margin-bottom: 0;
}

.stock-empty-state {
  padding: 24px 12px;
  text-align: center;
  color: #6f8a84;
}

.stock-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 130px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.stock-status-badge.is-empty {
  background: rgba(214, 58, 58, 0.12);
  color: #bf3030;
}

.stock-status-badge.is-low {
  background: rgba(240, 145, 31, 0.14);
  color: #b86e12;
}

.stock-status-badge.is-high {
  background: rgba(43, 120, 228, 0.12);
  color: #2d6fd0;
}

.stock-status-badge.is-unconfigured,
.stock-status-badge.is-ok {
  background: rgba(0, 142, 117, 0.1);
  color: #0d6555;
}

@media (max-width: 991px) {
  .stock-suggestion-summary {
    grid-template-columns: 1fr;
  }

  .stock-filters,
  .stock-page-hero {
    grid-template-columns: 1fr;
    display: grid;
  }
}
</style>
